/**
 * @file ResizeHandle.tsx
 * @description Вертикальная ручка-разделитель между колонками: тянется мышью
 * и пальцем, двойной клик — ширина по умолчанию, клавиатура — стрелки ±шаг,
 * Home/End — минимум/максимум, Enter — по умолчанию (phase19.16, партия
 * после приёмки Р2а; первый потребитель — конструктор сайта).
 *
 * Сама ручка занимает 8 px сетки (линия 1 px, на наведении и во время тяги —
 * 2 px цвета действия), зона захвата шире — 16 px, а при сенсорном вводе
 * (`pointer: coarse`) — 32 px, чтобы попадать пальцем на планшете.
 * Фокус с клавиатуры — та же линия 2 px цвета действия плюс мягкое свечение
 * вокруг линии; кольца вокруг всей зоны захвата нет (читалось «тройной
 * линией», QA SB122). Во время тяги поверх всего окна лежит прозрачный слой: iframe
 * канвы иначе забирает мышь себе, а выделение текста на странице выключено.
 *
 * Свёртка (`onCollapse`): протянули за минимум дальше `collapsePull` px —
 * колонка держится на минимуме, линия ручки становится пунктирной, по
 * отпусканию колонка сворачивается (ширина в памяти остаётся прежней).
 *
 * Ширина считается от ширины на старте: `direction = 1` — колонка слева от
 * ручки (тянем вправо — шире), `-1` — колонка справа (тянем вправо — уже).
 */

import { useEffect, useRef, useState, type KeyboardEvent, type PointerEvent } from "react";
import { createPortal } from "react-dom";

import { cn } from "@/lib/utils";

interface Props {
  /** Подпись для чтеца экрана: «Ширина навигатора». */
  label: string;
  /** Ширина колонки сейчас, px. */
  value: number;
  min: number;
  max: number;
  direction: 1 | -1;
  /** Шаг стрелок, px. */
  step?: number;
  /** Ширина во время тяги (часто, без записи в память). */
  onLive: (px: number) => void;
  /** Итоговая ширина: отпустили ручку или нажали клавишу. */
  onCommit: (px: number) => void;
  /** Ширина по умолчанию. */
  onReset: () => void;
  /** Свернуть колонку, если протянули за минимум; без него — свёртки нет. */
  onCollapse?: () => void;
  /** Насколько протянуть за минимум, чтобы свернуть, px. */
  collapsePull?: number;
  className?: string;
}

interface DragStart {
  pointerId: number;
  x: number;
  value: number;
}

export function ResizeHandle(props: Props) {
  const { label, value, min, max, direction, step = 16, onLive, onCommit, onReset, onCollapse, collapsePull = 40, className } = props;
  const start = useRef<DragStart | null>(null);
  const last = useRef(value);
  // Протянули за минимум — по отпусканию свернуть.
  const [collapsing, setCollapsing] = useState(false);
  const collapsingRef = useRef(false);
  // Ширина во время тяги (с первого сдвига) — для aria-valuenow и слоя поверх
  // окна; перерисовывается одна ручка. До сдвига слоя нет, чтобы двойной
  // щелчок доходил до ручки.
  const [dragValue, setDragValue] = useState<number | null>(null);
  const dragging = dragValue !== null;
  const clamp = (px: number) => Math.min(max, Math.max(min, Math.round(px)));

  // Пока тянем — текст на странице не выделяется, курсор «ширина» везде.
  useEffect(() => {
    if (!dragging) return;
    const body = document.body.style;
    const prev = { userSelect: body.userSelect, cursor: body.cursor };
    body.userSelect = "none";
    body.cursor = "col-resize";
    return () => {
      body.userSelect = prev.userSelect;
      body.cursor = prev.cursor;
    };
  }, [dragging]);

  const onPointerDown = (e: PointerEvent<HTMLDivElement>) => {
    if (e.button !== 0) return;
    e.preventDefault();
    // В jsdom и старых движках захвата нет — тогда события ловит слой поверх окна.
    e.currentTarget.setPointerCapture?.(e.pointerId);
    start.current = { pointerId: e.pointerId, x: e.clientX, value };
    last.current = value;
  };

  const onPointerMove = (e: PointerEvent<HTMLElement>) => {
    const s = start.current;
    if (!s || s.pointerId !== e.pointerId) return;
    if (e.clientX === s.x && last.current === s.value) return;
    const raw = s.value + direction * (e.clientX - s.x);
    const next = clamp(raw);
    // Первый сдвиг включает слой, даже если упёрлись в границу.
    setDragValue(next);
    if (onCollapse) {
      collapsingRef.current = raw < min - collapsePull;
      setCollapsing(collapsingRef.current);
    }
    if (next === last.current) return;
    last.current = next;
    onLive(next);
  };

  /** Конец тяги: отпустили — в память (если сдвинули), отменили — вернуть как было. */
  const finish = (keep: boolean) => {
    const s = start.current;
    if (!s) return;
    start.current = null;
    setDragValue(null);
    const fold = collapsingRef.current;
    collapsingRef.current = false;
    setCollapsing(false);
    if (keep && fold && onCollapse) {
      // Ширина в памяти прежняя: развернули — колонка как до свёртки.
      onLive(s.value);
      onCollapse();
    } else if (!keep) onLive(s.value);
    else if (last.current !== s.value) onCommit(last.current);
  };

  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    let next: number | null = null;
    if (e.key === "ArrowLeft") next = value - direction * step;
    else if (e.key === "ArrowRight") next = value + direction * step;
    else if (e.key === "Home") next = min;
    else if (e.key === "End") next = max;
    else if (e.key === "Enter") {
      e.preventDefault();
      onReset();
      return;
    }
    if (next === null) return;
    e.preventDefault();
    const px = clamp(next);
    if (px !== value) onCommit(px);
  };

  return (
    <div className={cn("relative h-full w-2", className)}>
      <div
        role="separator"
        aria-orientation="vertical"
        aria-label={label}
        aria-valuenow={dragValue ?? value}
        aria-valuemin={min}
        aria-valuemax={max}
        tabIndex={0}
        title={collapsing ? "Отпустите, чтобы свернуть" : "Потяните, чтобы изменить ширину. Двойной щелчок — как было"}
        data-dragging={dragging || undefined}
        data-collapsing={collapsing || undefined}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={() => finish(true)}
        onPointerCancel={() => finish(false)}
        onLostPointerCapture={() => finish(true)}
        onDoubleClick={onReset}
        onKeyDown={onKeyDown}
        className="group absolute inset-y-0 -left-1 -right-1 z-10 flex [@media(pointer:coarse)]:-left-3 [@media(pointer:coarse)]:-right-3 cursor-col-resize touch-none justify-center outline-none"
      >
        <span
          aria-hidden
          className="h-full w-px bg-outline-variant transition-[width,background-color] group-hover:w-0.5 group-hover:bg-primary group-focus-visible:w-0.5 group-focus-visible:bg-primary group-focus-visible:[box-shadow:0_0_0_2px_color-mix(in_srgb,var(--primary)_28%,transparent)] group-data-[dragging]:w-0.5 group-data-[dragging]:bg-primary group-data-[collapsing]:w-0 group-data-[collapsing]:border-l-2 group-data-[collapsing]:border-dashed group-data-[collapsing]:border-primary group-data-[collapsing]:bg-transparent"
        />
      </div>
      {dragging &&
        createPortal(
          <div
            data-testid="resize-overlay"
            aria-hidden
            className="fixed inset-0 z-[100] cursor-col-resize"
            onPointerMove={onPointerMove}
            onPointerUp={() => finish(true)}
            onPointerCancel={() => finish(false)}
          />,
          document.body,
        )}
    </div>
  );
}
